/**
 * Feature Settings Service
 * Manages feature flags stored in Firestore for admin opt-in/opt-out of features.
 * Path: data/sms/schools/{tenantId}/settings/features
 */

import { Injectable, inject } from '@angular/core';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TenantService } from './tenant.service';

export interface FeatureFlags {
  admissions: boolean;
  attendance: boolean;
  busyBees: boolean;
  payments: boolean;
  announcements: boolean;
  activities: boolean;
  dailyReports: boolean;
  updatedAt?: Date | any;
  updatedBy?: string;
}

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  admissions: false,
  attendance: false,
  busyBees: false,
  payments: false,
  announcements: false,
  activities: false,
  dailyReports: false,
};

export interface FeatureInfo {
  key: keyof Omit<FeatureFlags, 'updatedAt' | 'updatedBy'>;
  label: string;
  description: string;
  icon: string;
  tag?: string;
}

export const AVAILABLE_FEATURES: FeatureInfo[] = [
  {
    key: 'admissions',
    label: 'Admissions',
    description: 'Online admission forms, application tracking, and enrollment workflow for new students.',
    icon: 'clipboard-list',
  },
  {
    key: 'attendance',
    label: 'Attendance',
    description: 'Morning and afternoon session attendance tracking with class-wise reports and trend analytics.',
    icon: 'clipboard-check',
    tag: 'New',
  },
  {
    key: 'busyBees',
    label: 'Busy Bees',
    description: 'Teachers share child achievements, photos, and milestones. Parents view a curated activity feed.',
    icon: 'camera',
  },
  {
    key: 'payments',
    label: 'Payments',
    description: 'Fee management, payment tracking, invoicing, and grade-wise fee mapping for the school.',
    icon: 'wallet',
  },
  {
    key: 'announcements',
    label: 'Announcements',
    description: 'Broadcast school-wide announcements to parents, teachers, and staff.',
    icon: 'megaphone',
  },
  {
    key: 'activities',
    label: 'Activities',
    description: 'Teachers log daily activities (meals, naps, learning, play) and parents view the timeline.',
    icon: 'gallery',
  },
  {
    key: 'dailyReports',
    label: 'Daily Reports',
    description: 'Teachers log daily child progress (meals, naps, mood, learning) and parents see reports with trends.',
    icon: 'clipboard-check',
    tag: 'New',
  },
];

@Injectable({
  providedIn: 'root',
})
export class FeatureSettingsService {
  private firestore = inject(Firestore);
  private tenantService = inject(TenantService);

  private getFeaturesPath(): string {
    const collectionPath = this.tenantService.getCollectionName('settings');
    return `${collectionPath}/features`;
  }

  getFeatures(): Observable<FeatureFlags> {
    const featuresPath = this.getFeaturesPath();
    const featuresRef = doc(this.firestore, featuresPath);

    return from(getDoc(featuresRef)).pipe(
      map((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<FeatureFlags>;
          return { ...DEFAULT_FEATURE_FLAGS, ...data };
        }
        return { ...DEFAULT_FEATURE_FLAGS };
      }),
      catchError((error) => {
        console.error('[FeatureSettingsService] Error getting features:', error);
        return of({ ...DEFAULT_FEATURE_FLAGS });
      })
    );
  }

  saveFeatures(features: FeatureFlags): Observable<void> {
    const featuresPath = this.getFeaturesPath();
    const featuresRef = doc(this.firestore, featuresPath);

    const saveData = {
      ...features,
      updatedAt: new Date(),
    };

    return from(setDoc(featuresRef, saveData, { merge: true })).pipe(
      catchError((error) => {
        console.error('[FeatureSettingsService] Error saving features:', error);
        throw new Error(`Failed to save feature settings: ${error.message}`);
      })
    );
  }

  /**
   * Check if a specific feature is enabled
   */
  isFeatureEnabled(feature: keyof Omit<FeatureFlags, 'updatedAt' | 'updatedBy'>): Observable<boolean> {
    return this.getFeatures().pipe(
      map((flags) => flags[feature] ?? false)
    );
  }
}
